import React, { FC } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/utils/common'

import { NoticeBar } from './NoticeBar'
import { ViewerHeader } from './ViewerHeader'

export interface ViewerSurfaceProps {
  documentId?: string
  scrollContainerRef?: React.RefObject<HTMLDivElement>
  isLoading?: boolean
  children: React.ReactNode
  className?: string
  scrollClassName?: string
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const ViewerSurface: FC<ViewerSurfaceProps> = ({
  documentId,
  scrollContainerRef,
  isLoading,
  children,
  className,
  scrollClassName,
  onMouseDown,
  onClick,
}) => {
  return (
    <div className="bg-gray-50 p-4 md:p-6 h-full w-full overflow-hidden">
      <div
        className={cn(
          'w-full h-full min-h-0 bg-white overflow-hidden',
          'shadow-md flex flex-col transition-shadow duration-200',
          'border-solid border border-primary',
          'md:rounded-lg',
          className
        )}
      >
        {documentId && (
          <>
            <ViewerHeader documentId={documentId} />
            <NoticeBar
              documentId={documentId}
              scrollContainerRef={scrollContainerRef}
            />

            {isLoading && (
              <div className="sticky top-0 z-10 bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-2 text-sm text-blue-700">
                <Spinner className="h-4 w-4" />
                <span>Refreshing document...</span>
              </div>
            )}
          </>
        )}

        {scrollContainerRef
          ? (() => {
              const interactive = !!onClick || !!onMouseDown
              const divProps: React.HTMLAttributes<HTMLDivElement> = {
                className: cn('flex-1 overflow-auto', scrollClassName),
                onMouseDown,
                onClick,
              }

              if (interactive) {
                divProps.role = onClick ? 'button' : 'application'
                divProps.tabIndex = 0
                divProps['aria-label'] = 'Document viewer'
                divProps.onKeyDown = onClick
                  ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onClick(
                          e as unknown as React.MouseEvent<HTMLDivElement>
                        )
                      }
                    }
                  : undefined
              }

              return (
                <div ref={scrollContainerRef} {...divProps}>
                  {children}
                </div>
              )
            })()
          : children}
      </div>
    </div>
  )
}
